// src/modules/admin/pages/AccessHubManagerPage.tsx
// Painel Admin para gerenciar Centrais de Acesso

import React, { useState, useEffect } from 'react';
import { motion, Reorder } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Save, 
  Eye, 
  EyeOff, 
  GripVertical,
  Settings,
  Building2,
  Loader2,
  Check,
  RotateCcw,
  ChevronDown
} from 'lucide-react';
import { 
  AccessButton, 
  AccessHubConfig,
  getIconComponent,
  createEmptyButton,
  iconMap,
  tenantTypeTemplates
} from '../../access/accessConfig';
import {
  getAdminAccessHub,
  saveAdminAccessHub,
  getTenantAccessHub,
  saveTenantAccessHub,
  addButton,
  updateButton,
  removeButton,
} from '../../access/accessService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../core/services/firebase/config';

type EditorMode = 'admin' | 'templates' | 'tenant';

interface TenantOption {
  id: string;
  name: string;
  type: string;
}

export function AccessHubManagerPage() {
  const [mode, setMode] = useState<EditorMode>('admin');
  const [config, setConfig] = useState<AccessHubConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('pousada');

  // Carregar config baseado no modo
  useEffect(() => {
    loadConfig();
  }, [mode, selectedTenant, selectedTemplate]);

  // Carregar lista de tenants
  useEffect(() => {
    async function loadTenants() {
      try {
        const snapshot = await getDocs(collection(db, 'tenants'));
        const tenantList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().businessName || doc.id,
          type: doc.data().tenantType || 'pousada'
        }));
        setTenants(tenantList);
      } catch (error) {
        console.error('Erro ao carregar tenants:', error);
      }
    }
    loadTenants();
  }, []);

  async function loadConfig() {
    setLoading(true);
    try {
      if (mode === 'admin') {
        const adminConfig = await getAdminAccessHub();
        setConfig(adminConfig);
      } else if (mode === 'templates') {
        setConfig({
          buttons: tenantTypeTemplates[selectedTemplate] || [],
          customized: false,
        });
      } else if (mode === 'tenant' && selectedTenant) {
        const tenant = tenants.find(t => t.id === selectedTenant);
        const tenantConfig = await getTenantAccessHub(selectedTenant, tenant?.type);
        setConfig(tenantConfig);
      }
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!config) return;
    setSaving(true);
    try {
      if (mode === 'admin') {
        await saveAdminAccessHub(config);
      } else if (mode === 'tenant' && selectedTenant) {
        await saveTenantAccessHub(selectedTenant, config);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Erro ao salvar:', error);
    } finally {
      setSaving(false);
    }
  }

  function handleAddButton() {
    if (!config) return;
    const newButton = createEmptyButton(config.buttons.length + 1);
    setConfig(addButton(config, newButton));
  }

  function handleUpdateButton(buttonId: string, updates: Partial<AccessButton>) {
    if (!config) return;
    setConfig(updateButton(config, buttonId, updates));
  }

  function handleRemoveButton(buttonId: string) {
    if (!config) return;
    setConfig(removeButton(config, buttonId));
  }

  function handleReorder(newOrder: AccessButton[]) {
    if (!config) return;
    setConfig({
      ...config,
      buttons: newOrder.map((btn, index) => ({ ...btn, order: index + 1 })),
      customized: true,
    });
  }

  return (
    <div className="min-h-screen bg-brand-midnight p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white font-display uppercase tracking-tight">
            Gerenciar Centrais de Acesso
          </h1>
          <p className="text-white/40 mt-2">
            Configure os botões que aparecem nas centrais de acesso
          </p>
        </div>

        {/* Mode Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'admin', label: 'Admin Global', icon: Settings },
            { id: 'templates', label: 'Templates', icon: Building2 },
            { id: 'tenant', label: 'Por Cliente', icon: Building2 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setMode(tab.id as EditorMode)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                mode === tab.id 
                  ? 'bg-brand-gold text-brand-midnight' 
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Selector for Templates/Tenants */}
        {mode === 'templates' && (
          <div className="mb-6">
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="bg-white/5 border border-white/10 text-white px-4 py-2 rounded-xl"
            >
              {Object.keys(tenantTypeTemplates).map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>
        )}

        {mode === 'tenant' && (
          <div className="mb-6">
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="bg-white/5 border border-white/10 text-white px-4 py-2 rounded-xl"
            >
              <option value="">Selecione um cliente...</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>{tenant.name} ({tenant.type})</option>
              ))}
            </select>
          </div>
        )}

        {/* Editor */}
        <div className="bg-brand-charcoal/30 border border-white/10 rounded-2xl p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-brand-gold" size={32} />
            </div>
          ) : config ? (
            <>
              {/* Buttons List */}
              <Reorder.Group 
                axis="y" 
                values={config.buttons} 
                onReorder={handleReorder}
                className="space-y-3"
              >
                {config.buttons.map((button) => (
                  <Reorder.Item key={button.id} value={button}>
                    <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                      <GripVertical className="text-white/20 cursor-grab" size={18} />
                      
                      {/* Icon Select */}
                      <select
                        value={button.icon}
                        onChange={(e) => handleUpdateButton(button.id, { icon: e.target.value })}
                        className="bg-white/10 text-white px-2 py-1 rounded text-sm"
                      >
                        {Object.keys(iconMap).map(iconName => (
                          <option key={iconName} value={iconName}>{iconName}</option>
                        ))}
                      </select>

                      {/* Label */}
                      <input
                        type="text"
                        value={button.label}
                        onChange={(e) => handleUpdateButton(button.id, { label: e.target.value })}
                        className="flex-1 bg-transparent text-white font-bold focus:outline-none"
                        placeholder="Label"
                      />

                      {/* Path */}
                      <input
                        type="text"
                        value={button.path}
                        onChange={(e) => handleUpdateButton(button.id, { path: e.target.value })}
                        className="w-40 bg-white/10 text-white/60 px-2 py-1 rounded text-sm"
                        placeholder="/caminho"
                      />

                      {/* Visibility */}
                      <button
                        onClick={() => handleUpdateButton(button.id, { visible: !button.visible })}
                        className={`p-2 rounded-lg ${button.visible ? 'text-green-400' : 'text-gray-400'}`}
                        title={button.visible ? 'Ocultar' : 'Mostrar'}
                      >
                        {button.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleRemoveButton(button.id)}
                        className="p-2 text-red-400/60 hover:text-red-400 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>

              {/* Add Button */}
              <button
                onClick={handleAddButton}
                className="w-full mt-4 p-4 border-2 border-dashed border-white/20 rounded-xl text-white/40 hover:text-white hover:border-white/40 transition-all flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Adicionar Botão
              </button>

              {/* Save Button */}
              {(mode === 'admin' || mode === 'tenant') && (
                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-brand-gold text-brand-midnight font-bold rounded-xl hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : saved ? (
                      <Check size={18} />
                    ) : (
                      <Save size={18} />
                    )}
                    {saved ? 'Salvo!' : 'Salvar Alterações'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-white/40 text-center py-8">Selecione um item para editar</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccessHubManagerPage;
